import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Scrivito from "scrivito";
import { Button, Container } from "reactstrap";
import { show } from "@ebay/nice-modal-react";
import { debounce } from "lodash-es";
import { useQuery } from "urql";
import { getHomepage } from "utils";
import {
  Icon,
  ObjValidator,
  ProjectDetailsModal,
  SearchInput,
  ItemsPerPageSelect,
  Select,
} from "components";
import { ConfirmationModal } from "components/_Modals/ConfirmationModal";
import { PROJECTS_PER_PAGE } from "variables";
import { useItemsPerPage, useTranslate, useUserDepartments } from "hooks";
import { ProjectListAndStatusesQry } from "api/queries";
import { MultipleProjectsDeleteMut, SameProjectCreateMut } from "api/mutations";
import { TableFooter } from "components/TableFooter";
import { useNavigateToProject } from "hooks/projectHooks";
import { ProjectListTable } from "./SubComponents/ProjectListTable";
import { ProjectListFilters } from "./SubComponents/ProjectListFilters";
import { useLanguage } from "stores/globalStore";

const ProjectListSame = Scrivito.connect(({ page }) => {
  const translate = useTranslate();
  const navigateToProject = useNavigateToProject();
  const {
    options: departmentsOptions,
    selected: selectedDepartment,
    setSelected: setSelectedDepartment,
  } = useUserDepartments();

  const {
    itemsPerPage,
    handleItemsPerPageChange,
    currentPageNo,
    setCurrentPageNo,
  } = useItemsPerPage(PROJECTS_PER_PAGE);

  const language = useLanguage();

  const home = getHomepage();

  const { projectId } = Scrivito.currentPageParams();
  const [isOrderDescending, setIsOrderDescending] = useState(true);
  const [statusColors, setStatusColors] = useState({});
  const order = isOrderDescending ? "updatedAt desc" : "updatedAt asc";
  const [filters, setFilters] = useState([]);
  const [searchQuery, setSearchQuery] = useState(null);
  const [sharedStatusSwitcher, setSharedStatusSwitcher] = useState(null);
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [projectsToDelete, setProjectsToDelete] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);

  const [projectsDataReq, refetchProjectList] = useQuery({
    query: ProjectListAndStatusesQry,
    variables: {
      limit: itemsPerPage,
      page: currentPageNo,
      order,
      filters: {
        searchQuery,
        sharedStatusSwitcher,
        statuses: filterStatuses,
        departmentId: selectedDepartment?.value || "",
      },
    },
  });

  const { data: projectsData, fetching, stale, error } = projectsDataReq;

  const { projectStatuses, currentUser } = projectsData || {};
  const { projectList: projectListObj } = currentUser || {};

  const {
    pages: pagesCount = 0,
    items: projects = [],
    overallStatistics,
    statistics,
    projectsCount: filteredCount,
  } = projectListObj || {};

  // resets projectFilters when overallStatistics updates
  const resetCallback = useCallback(() => setFilters([]), []);
  const isLoading = fetching || stale;

  const projectsCount = useMemo(
    () =>
      overallStatistics
        ? overallStatistics.shared + overallStatistics.notShared
        : 0,
    [overallStatistics],
  );

  useEffect(() => {
    if (projectStatuses?.length) {
      setStatusColors(
        projectStatuses.reduce((result, current) => {
          result[current.status] = current.color;
          return result;
        }, {}),
      );
    }
  }, [projectStatuses]);

  const handleSearchQuery = useCallback(
    ({ target: { value } }) => {
      const isValidQuery = value && value.length >= 3;
      if (
        (!searchQuery && !isValidQuery) ||
        (searchQuery?.length > 2 && !isValidQuery && value.length > 0)
      ) {
        return;
      }
      setSearchQuery(value || null);
      if (value) {
        Scrivito.navigateTo(() => Scrivito.currentPage(), {
          params: {
            page: "1",
          },
        });
      }
    },
    [searchQuery],
  );

  const skeletonCount = useMemo(
    () =>
      +currentPageNo !== +pagesCount
        ? itemsPerPage
        : projectsCount - itemsPerPage * (currentPageNo - 1),
    [currentPageNo, pagesCount, projectsCount, itemsPerPage],
  );

  const projectItemObj = page.get("redirectToProjectItem") || page;

  const toggleModal = useCallback(() => {
    show(ProjectDetailsModal, {
      mutation: SameProjectCreateMut,
      resetCallback,
    }).then(({ id }) => navigateToProject.configurator(id));
  }, [navigateToProject, resetCallback]);

  const resetUrlParams = useCallback(() => {
    const projectRefs = home?.get("configLeftSidebarProjectRefs");
    const projectList = projectRefs[0];
    Scrivito.navigateTo(projectList);
  }, [home]);

  const handleSelectDepartment = (dept) => {
    if (dept.value !== selectedDepartment.value) {
      setSelectedDepartment(dept);
      Scrivito.navigateTo(Scrivito.currentPage(), { params: { page: 1 } });
    }
  };

  const handleProjectsDelete = useCallback(() => {
    const projectsToDeleteList = projects
      .filter((proj) => projectsToDelete.includes(proj.id))
      .map((proj) => ({ id: proj.id, title: proj.title }));
    show(ConfirmationModal, {
      submitBtnText: translate("PROJECT_LIST_DELETE_PROJECTS"),
      title: translate("PROJECT_LIST_DELETE_PROJECTS"),
      alertText: translate("MULPIPLE_PROJECTS_MODAL_DELETE_WARNING_TEXT"),
      displayList: projectsToDeleteList,
      mutation: MultipleProjectsDeleteMut,
      params: { input: { ids: projectsToDelete, languageCode: language } },
    }).then(() => {
      // setTimeout is used for update projectList properly, due to DS-cache needs some time to update the data
      setTimeout(() => {
        setProjectsToDelete([]);
        setIsSelectAll(false);
        refetchProjectList({
          variables: {
            page:
              projectsToDelete.length === projects.length
                ? String(currentPageNo - 1)
                : String(currentPageNo),
          },
          requestPolicy: "network-only",
        });
        if (projectId && projectsToDelete.includes(projectId)) {
          resetUrlParams();
        }
        resetCallback();
      }, 2000);
    });
  }, [
    projects,
    translate,
    projectsToDelete,
    refetchProjectList,
    currentPageNo,
    projectId,
    resetCallback,
    resetUrlParams,
    language,
  ]);

  const handleSetCurrentPage = useCallback(
    (pageToSet) => {
      setCurrentPageNo(pageToSet > pagesCount ? pageToSet - 1 : pageToSet || 1);
    },
    [setCurrentPageNo, pagesCount],
  );

  return (
    <div className="position-relative bg-gray-100">
      <Container className="pb-8">
        <div className="d-flex justify-content-between align-items-center py-5_5">
          <h3 className="text-uppercase mb-0" data-testid="MY_PROJECTS">
            {translate("MY_PROJECTS")}
          </h3>
          <Button
            data-testid="NEW_PROJECT_BUTTON_TEXT"
            onClick={toggleModal}
            color="primary"
            className="d-flex align-items-center"
          >
            <Icon
              prefix="trox"
              iconName="plus-regular"
              className="icon-16 me-1"
            />
            {translate("NEW_PROJECT_BUTTON_TEXT")}
          </Button>
        </div>
        <div className="d-flex justify-content-between align-items-start py-3">
          <div className="col-3">
            <Select
              onChange={handleSelectDepartment}
              options={departmentsOptions}
              value={selectedDepartment}
              name="departmentSelect"
              testId="department-select"
            />
          </div>
          <div className="col-3 d-flex flex-row justify-content-end align-items-center">
            {projectsToDelete.length > 0 ? (
              <div className="col-3 me-5">
                <Button
                  data-testid="PROJECT_LIST_DELETE_PROJECTS"
                  className="btn btn-secondary"
                  onClick={handleProjectsDelete}
                >
                  {translate("PROJECT_LIST_DELETE_PROJECTS")}
                </Button>
              </div>
            ) : (
              <SearchInput
                onChange={debounce(handleSearchQuery, 1000)}
                data-testid="SEARCH_FIELD"
                classNames="w-100"
                placeHolder={translate("SEARCH_FIELD_PLACEHOLDER")}
              />
            )}
          </div>
        </div>
        <div className="d-flex flex-row justify-content-between">
          <div className="flex-shrink-0">
            <ItemsPerPageSelect
              itemsPerPage={itemsPerPage}
              onChange={handleItemsPerPageChange}
            />
          </div>
          <div className="d-flex flex-nowrap justify-content-end gap-2 flex-grow-1 ms-3">
            <ProjectListFilters
              overallStatistics={overallStatistics}
              projectsCount={projectsCount}
              setSearchQuery={setSearchQuery}
              setSharedStatusSwitcher={setSharedStatusSwitcher}
              projectStatuses={projectStatuses}
              setFilterStatuses={setFilterStatuses}
              filters={filters}
              setFilters={setFilters}
              statistics={statistics}
              setIsSelectAll={setIsSelectAll}
              setProjectsToDelete={setProjectsToDelete}
              setCurrentPageNo={setCurrentPageNo}
            />
          </div>
        </div>
        <div className="px-4 pt-2_5 pb-4 bg-white">
          <ProjectListTable
            currentPageNo={currentPageNo}
            error={error}
            fetching={isLoading}
            isOrderDescending={isOrderDescending}
            projects={projects}
            projectItemObj={projectItemObj}
            refetchProjectList={refetchProjectList}
            setIsOrderDescending={setIsOrderDescending}
            skeletonCount={skeletonCount}
            statusColors={statusColors}
            projectsToDelete={projectsToDelete}
            setProjectsToDelete={setProjectsToDelete}
            isSelectAll={isSelectAll}
            setIsSelectAll={setIsSelectAll}
            resetUrlParams={resetUrlParams}
            setCurrentPageNo={setCurrentPageNo}
          />

          <TableFooter
            filteredCount={filteredCount}
            pagesCount={pagesCount}
            currentPageNo={currentPageNo}
            itemsPerPage={itemsPerPage}
            handleSetCurrentPage={handleSetCurrentPage}
          />
        </div>
      </Container>
    </div>
  );
});

const CheckedPage = (props) => (
  <ObjValidator {...props}>
    <ProjectListSame {...props} />
  </ObjValidator>
);
export default CheckedPage;
Scrivito.provideComponent("ProjectListSame", CheckedPage);
